import { useState } from 'react';
import {
  Container,
  Title,
  Text,
  Card,
  Stack,
  Group,
  Badge,
  Button,
  Divider,
  Alert,
  Modal,
  Select,
  ActionIcon,
  Tooltip,
  Textarea,
} from '@mantine/core';
import { useParams, useNavigate } from 'react-router-dom';
import {
  IconEdit,
  IconDownload,
  IconTrash,
  IconAlertCircle,
  IconCheck,
  IconCalendar,
  IconBuilding,
  IconCurrencyDollar,
  IconClock,
  IconFileText,
  IconX,
} from '@tabler/icons-react';
import { useSingleSourceForm, useUpdateSingleSourceForm, useDeleteSingleSourceForm, useSubmitSingleSourceForm, useDownloadSingleSourceDocument, useGenerateSingleSourceFormPDF, useApproveSingleSourceForm, useRejectSingleSourceForm, hasSingleSourceUploadedDocument } from '../hooks/useSingleSource';
import { SingleSourceDocumentUpload } from '../components/SingleSourceDocumentUpload';
import { useBidCommitteeMeetings } from '../../bidCommittee/hooks';
import { useAuth } from '../../../auth/AuthContext';
import {
  approvalDisplayName,
  dhivehiDisplayStyle,
  farumaFieldDisplayStyle,
  singleSourceRtlLayoutStyle,
} from '../../../utils/dhivehiText';
import { SINGLE_SOURCE_FIELD_LABELS } from '../../../utils/singleSourceFieldLabels';
import { canEditSingleSourceForm } from '../permissions';

const L = SINGLE_SOURCE_FIELD_LABELS;

function FieldLabel({ children }: { children: string }) {
  return (
    <Text size="sm" c="dimmed" style={farumaFieldDisplayStyle()}>
      {children}
    </Text>
  );
}

const statusColors: Record<string, string> = {
  DRAFT: 'gray',
  SUBMITTED: 'blue',
  UNDER_REVIEW: 'yellow',
  APPROVED: 'green',
  REJECTED: 'red',
  AWARDED: 'purple',
};

export function SingleSourceDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [meetingModalOpen, setMeetingModalOpen] = useState(false);
  const [selectedMeetingId, setSelectedMeetingId] = useState<string>('');
  const [approveModalOpen, setApproveModalOpen] = useState(false);
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [approvalComments, setApprovalComments] = useState('');
  const [rejectionComments, setRejectionComments] = useState('');

  const { data: form, isLoading, error } = useSingleSourceForm(id!);
  const { data: meetings } = useBidCommitteeMeetings();
  const updateForm = useUpdateSingleSourceForm();
  const deleteForm = useDeleteSingleSourceForm();
  const submitForm = useSubmitSingleSourceForm();
  const downloadDocument = useDownloadSingleSourceDocument();
  const generatePDF = useGenerateSingleSourceFormPDF();
  const approveForm = useApproveSingleSourceForm();
  const rejectForm = useRejectSingleSourceForm();

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'MVR',
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const handleStatusUpdate = (status: string) => {
    if (form) {
      updateForm.mutate(
        { id: form.id, data: { status: status as any } },
        {
          onSuccess: () => {
            setEditModalOpen(false);
          },
        }
      );
    }
  };

  const handleSubmitForReview = () => {
    if (form) {
      submitForm.mutate(form.id);
    }
  };

  const handleDelete = () => {
    if (form) {
      deleteForm.mutate(form.id, {
        onSuccess: () => {
          navigate('/single-source');
        },
      });
    }
  };

  const handleAssignToMeeting = () => {
    if (form && selectedMeetingId) {
      updateForm.mutate(
        { id: form.id, data: { meetingId: selectedMeetingId } },
        {
          onSuccess: () => {
            setMeetingModalOpen(false);
            setSelectedMeetingId('');
          },
        }
      );
    }
  };

  const handleDownload = () => {
    if (form?.doc && hasSingleSourceUploadedDocument(form.doc)) {
      downloadDocument.mutate({ id: form.id, fileName: form.doc.fileName });
    }
  };

  const hasUploadedDocument = form?.doc ? hasSingleSourceUploadedDocument(form.doc) : false;

  const handleGeneratePDF = () => {
    if (form) {
      generatePDF.mutate(form.id);
    }
  };

  const handleApprove = () => {
    if (form) {
      approveForm.mutate(
        { id: form.id, comments: approvalComments || undefined },
        {
          onSuccess: () => {
            setApproveModalOpen(false);
            setApprovalComments('');
          },
        }
      );
    }
  };

  const handleReject = () => {
    if (form && rejectionComments.trim()) {
      rejectForm.mutate(
        { id: form.id, comments: rejectionComments },
        {
          onSuccess: () => {
            setRejectModalOpen(false);
            setRejectionComments('');
          },
        }
      );
    }
  };

  // Check if current user can approve/reject
  const canApprove = form?.approvals?.some(
    (approval) =>
      user?.role === 'HEAD_OF_PROCUREMENT' &&
      approval.stage === 'HEAD_OF_PROCUREMENT' &&
      approval.status === 'PENDING' &&
      approval.approver.id === user.id
  );

  const canEditAndResubmit = canEditSingleSourceForm(form, user);

  if (isLoading) {
    return (
      <Container size="xl" py="xl">
        <Text>Loading...</Text>
      </Container>
    );
  }

  if (error || !form) {
    return (
      <Container size="xl" py="xl">
        <Alert
          icon={<IconAlertCircle size={16} />}
          title="Error"
          color="red"
          variant="light"
        >
          Failed to load single source form. Please try again.
        </Alert>
      </Container>
    );
  }

  const approvals = form.approvals ?? [];
  const approvalCommentEntries = approvals.filter(
    (a) => a.status === 'APPROVED' && a.comments?.trim()
  );
  const rejectionCommentEntries = approvals.filter(
    (a) => a.status === 'REJECTED' && a.comments?.trim()
  );

  const approvalStageLabel = (stage: string) =>
    stage === 'HEAD_OF_PROCUREMENT' ? 'Head of Procurement' : stage;

  return (
    <Container size="xl" py="xl">
      <Stack gap="lg">
        <Group justify="space-between">
          <div>
            <Title order={2}>Single Source Procurement</Title>
            <Text fw={500} style={farumaFieldDisplayStyle()}>{form.projectName}</Text>
            <Text c="dimmed" size="sm">
              Created on {formatDate(form.createdAt)}
            </Text>
          </div>
          <Group>
            <Badge color={statusColors[form.status]} size="lg">
              {form.status.replace('_', ' ')}
            </Badge>
            {canEditAndResubmit && (
              <Button
                leftSection={<IconEdit size={16} />}
                variant="light"
                onClick={() => navigate(`/single-source/${form.id}/edit`)}
              >
                Edit
              </Button>
            )}
            <Button
              leftSection={<IconFileText size={16} />}
              variant="light"
              onClick={handleGeneratePDF}
              loading={generatePDF.isPending}
            >
              Generate PDF
            </Button>
            <Tooltip label="Delete">
              <ActionIcon
                color="red"
                variant="light"
                onClick={() => setDeleteModalOpen(true)}
              >
                <IconTrash size={16} />
              </ActionIcon>
            </Tooltip>
          </Group>
        </Group>

        {form.status === 'REJECTED' && rejectionCommentEntries.length > 0 && (
          <Alert color="red" title="Rejection comments" variant="light">
            <Stack gap="xs">
              {rejectionCommentEntries.map((approval) => (
                <div key={approval.id}>
                  <Text size="sm" fw={500}>
                    {approvalStageLabel(approval.stage)}
                    {approval.approver && (
                      <>
                        {' — '}
                        <Text
                          component="span"
                          style={dhivehiDisplayStyle(approvalDisplayName(approval.approver))}
                        >
                          {approvalDisplayName(approval.approver)}
                        </Text>
                      </>
                    )}
                  </Text>
                  <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>
                    {approval.comments}
                  </Text>
                </div>
              ))}
            </Stack>
          </Alert>
        )}

        {form.status === 'REJECTED' && canEditAndResubmit && (
          <Alert color="blue" title="Resubmit for approval" variant="light">
            <Text size="sm">
              Use <strong>Edit</strong> to update the form, then click{' '}
              <strong>Resubmit to Head of Procurement</strong> below when you are ready for another review.
            </Text>
          </Alert>
        )}

        {form.status === 'APPROVED' && approvalCommentEntries.length > 0 && (
          <Alert color="green" title="Approval comments" variant="light">
            <Stack gap="xs">
              {approvalCommentEntries.map((approval) => (
                <div key={approval.id}>
                  <Text size="sm" fw={500}>
                    {approvalStageLabel(approval.stage)}
                    {approval.approver && (
                      <>
                        {' — '}
                        <Text
                          component="span"
                          style={dhivehiDisplayStyle(approvalDisplayName(approval.approver))}
                        >
                          {approvalDisplayName(approval.approver)}
                        </Text>
                      </>
                    )}
                  </Text>
                  <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>
                    {approval.comments}
                  </Text>
                </div>
              ))}
            </Stack>
          </Alert>
        )}

        <Card shadow="sm" p="lg">
            <Stack gap="md" style={singleSourceRtlLayoutStyle()}>
              <Title order={4}>Form Details</Title>
              <Divider />
              
              <Group>
                <IconFileText size={20} color="gray" />
                <div>
                  <Text size="sm" c="dimmed">Form Number</Text>
                  <Text fw={500}>{form.formNumber}</Text>
                </div>
              </Group>

              <Group>
                <IconCalendar size={20} color="gray" />
                <div>
                  <FieldLabel>{L.date}</FieldLabel>
                  <Text fw={500}>{formatDate(form.date)}</Text>
                </div>
              </Group>

              <div>
                <FieldLabel>{L.title}</FieldLabel>
                <Text fw={500} style={farumaFieldDisplayStyle()}>{form.projectName}</Text>
              </div>

              <div>
                <FieldLabel>{L.projectLocation}</FieldLabel>
                <Text fw={500} style={farumaFieldDisplayStyle()}>{form.projectLocation}</Text>
              </div>

              <Group>
                <IconBuilding size={20} color="gray" />
                <div style={{ flex: 1 }}>
                  <FieldLabel>{L.vendorName}</FieldLabel>
                  <Text fw={500} style={farumaFieldDisplayStyle()}>{form.vendorName}</Text>
                </div>
              </Group>

              <Group>
                <IconCurrencyDollar size={20} color="gray" />
                <div>
                  <FieldLabel>{L.proposedCost}</FieldLabel>
                  <Text fw={500}>{formatCurrency(form.proposedCost)}</Text>
                </div>
              </Group>

              <Group>
                <IconClock size={20} color="gray" />
                <div>
                  <FieldLabel>{L.proposedDuration}</FieldLabel>
                  <Text fw={500}>{form.proposedDuration} days</Text>
                </div>
              </Group>
            </Stack>
        </Card>


        <Card shadow="sm" p="lg">
          <Stack gap="md">
            <Title order={4}>Budget Information</Title>
            <Divider />
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
              <div>
                <FieldLabel>{L.totalBudget}</FieldLabel>
                <Text fw={500} ta="right">{form.totalBudget ? formatCurrency(form.totalBudget) : 'Not specified'}</Text>
              </div>
              <div>
                <FieldLabel>{L.amountAllocatedFromCurrentYearBudget}</FieldLabel>
                <Text fw={500} ta="right">{form.amountAllocatedFromCurrentYearBudget ? formatCurrency(form.amountAllocatedFromCurrentYearBudget) : 'Not specified'}</Text>
              </div>
              <div>
                <FieldLabel>{L.totalExpenditureTillDate}</FieldLabel>
                <Text fw={500} ta="right">{form.totalExpenditureTillDate ? formatCurrency(form.totalExpenditureTillDate) : 'Not specified'}</Text>
              </div>
              <div>
                <FieldLabel>{L.remainingBudget}</FieldLabel>
                <Text fw={500} ta="right">{form.remainingBudget ? formatCurrency(form.remainingBudget) : 'Not specified'}</Text>
              </div>
            </div>
          </Stack>
        </Card>

        <Card shadow="sm" p="lg">
          <Stack gap="md" style={singleSourceRtlLayoutStyle()}>
            <Title order={4}>Justification & Conditions</Title>
            <Divider />
            <div>
              <FieldLabel>{L.justification}</FieldLabel>
              <Text style={{ whiteSpace: "pre-wrap", ...farumaFieldDisplayStyle() }}>{form.justification}</Text>
            </div>
            {form.detailsOfConditions && (
              <div>
                <Text size="sm" c="dimmed" mb="xs" style={farumaFieldDisplayStyle()}>
                  {L.detailsOfConditions}
                </Text>
                <Text style={{ whiteSpace: "pre-wrap", ...farumaFieldDisplayStyle() }}>
                  {form.detailsOfConditions}
                </Text>
              </div>
            )}
            {form.decisionToSingleSource && (
              <div>
                <FieldLabel>{L.decisionToSingleSource}</FieldLabel>
                <Text style={{ whiteSpace: "pre-wrap", ...farumaFieldDisplayStyle() }}>
                  {form.decisionToSingleSource}
                </Text>
              </div>
            )}
          </Stack>
        </Card>

        {form.meeting && (
          <Card shadow="sm" p="lg">
            <Stack gap="md">
              <Title order={4}>Bid Committee Meeting Details</Title>
              <Divider />
              <Group>
                <IconCalendar size={20} color="gray" />
                <div>
                  <Text size="sm" c="dimmed">Meeting Title</Text>
                  <Text fw={500}>{form.meeting.title}</Text>
                </div>
              </Group>
              <Group>
                <IconClock size={20} color="gray" />
                <div>
                  <Text size="sm" c="dimmed">Scheduled Date</Text>
                  <Text fw={500}>{formatDate(form.meeting.scheduledDate)}</Text>
                </div>
              </Group>
              {form.meeting.attendees && form.meeting.attendees.length > 0 && (
                <div>
                  <Text size="sm" c="dimmed">Attendees</Text>
                  <Group gap="xs" mt="xs">
                    {form.meeting.attendees.map((attendee, index) => (
                      <Badge key={index} variant="light" size="sm">
                        {attendee.member.fullName}
                      </Badge>
                    ))}
                  </Group>
                </div>
              )}
            </Stack>
          </Card>
        )}

        <Card shadow="sm" p="lg">
          <Stack gap="md">
            <Title order={4}>Document</Title>
            <Divider />
            {canEditAndResubmit ? (
              <>
                <SingleSourceDocumentUpload
                  formId={form.id}
                  currentFileName={hasUploadedDocument ? form.doc?.fileName : undefined}
                />
                {hasUploadedDocument && form.doc && (
                  <>
                    <Divider />
                    <Group justify="space-between" wrap="nowrap">
                      <Text size="sm" fw={500} style={{ flex: 1 }} truncate>
                        {form.doc.fileName}
                      </Text>
                      <Tooltip label="Download">
                        <ActionIcon
                          variant="light"
                          size="lg"
                          onClick={handleDownload}
                          loading={downloadDocument.isPending}
                          aria-label={`Download ${form.doc.fileName}`}
                        >
                          <IconDownload size={18} />
                        </ActionIcon>
                      </Tooltip>
                    </Group>
                  </>
                )}
              </>
            ) : (
              <>
                {hasUploadedDocument && form.doc ? (
                  <Group justify="space-between" wrap="nowrap">
                    <Text size="sm" fw={500} style={{ flex: 1 }} truncate>
                      {form.doc.fileName}
                    </Text>
                    <Tooltip label="Download">
                      <ActionIcon
                        variant="light"
                        size="lg"
                        onClick={handleDownload}
                        loading={downloadDocument.isPending}
                        aria-label={`Download ${form.doc.fileName}`}
                      >
                        <IconDownload size={18} />
                      </ActionIcon>
                    </Tooltip>
                  </Group>
                ) : (
                  <Text size="sm" c="dimmed">
                    No document uploaded.
                  </Text>
                )}
              </>
            )}
          </Stack>
        </Card>

        <Card shadow="sm" p="lg">
          <Stack gap="md">
            <Title order={4}>Status & Actions</Title>
            <Divider />

            <div>
              <Text size="sm" c="dimmed">Current Status</Text>
              <Badge color={statusColors[form.status]} size="lg" mt="xs">
                {form.status.replace('_', ' ')}
              </Badge>
            </div>

            {form.approvals && form.approvals.length > 0 && (
              <div>
                <Text size="sm" c="dimmed" mb="xs">Approval Status</Text>
                <Stack gap="xs">
                  {form.approvals.map((approval) => (
                    <div key={approval.id}>
                      <Group justify="space-between">
                        <Text size="sm" fw={500}>
                          {approvalStageLabel(approval.stage)}
                        </Text>
                        <Badge
                          color={
                            approval.status === 'APPROVED'
                              ? 'green'
                              : approval.status === 'REJECTED'
                              ? 'red'
                              : 'yellow'
                          }
                          size="sm"
                        >
                          {approval.status}
                        </Badge>
                      </Group>
                      {approval.approver && (
                        <Text size="xs" c="dimmed" style={dhivehiDisplayStyle(approvalDisplayName(approval.approver))}>
                          {approvalDisplayName(approval.approver)}
                        </Text>
                      )}
                      {approval.comments && (
                        <Text size="sm" mt="xs" style={{ whiteSpace: 'pre-wrap' }}>
                          {approval.status === 'REJECTED' ? 'Rejection comments: ' : 'Comments: '}
                          {approval.comments}
                        </Text>
                      )}
                      {approval.approvedAt && (
                        <Text size="xs" c="dimmed">
                          Approved: {formatDate(approval.approvedAt)}
                        </Text>
                      )}
                      {approval.rejectedAt && (
                        <Text size="xs" c="dimmed">
                          Rejected: {formatDate(approval.rejectedAt)}
                        </Text>
                      )}
                    </div>
                  ))}
                </Stack>
              </div>
            )}

            {form.meeting && (
              <div>
                <Text size="sm" c="dimmed">Assigned Meeting</Text>
                <Text fw={500}>{form.meeting.title}</Text>
                <Text size="sm" c="dimmed">
                  {formatDate(form.meeting.scheduledDate)}
                </Text>
              </div>
            )}

            {form.status === 'DRAFT' && canEditAndResubmit && (
              <Alert color="blue" title="Draft">
                This form is saved as a draft. Send it to Head of Procurement when you are ready for
                review.
              </Alert>
            )}

            <Stack gap="sm">
              {canEditAndResubmit && (
                <Button
                  leftSection={<IconCheck size={16} />}
                  onClick={handleSubmitForReview}
                  loading={submitForm.isPending}
                >
                  {form.status === 'REJECTED'
                    ? 'Resubmit to Head of Procurement'
                    : 'Send to Head of Procurement for Approval'}
                </Button>
              )}

              {canApprove && (
                <>
                  <Button
                    leftSection={<IconCheck size={16} />}
                    color="green"
                    onClick={() => setApproveModalOpen(true)}
                  >
                    Approve
                  </Button>
                  <Button
                    leftSection={<IconX size={16} />}
                    color="red"
                    variant="light"
                    onClick={() => setRejectModalOpen(true)}
                  >
                    Reject
                  </Button>
                </>
              )}

              <Button
                leftSection={<IconCalendar size={16} />}
                variant="light"
                onClick={() => setMeetingModalOpen(true)}
              >
                {form.meeting ? 'Reassign Meeting' : 'Assign to Meeting'}
              </Button>

              <Button
                leftSection={<IconEdit size={16} />}
                variant="light"
                onClick={() => setEditModalOpen(true)}
              >
                Update Status
              </Button>
            </Stack>
          </Stack>
        </Card>
      </Stack>

      {/* Status Update Modal */}
      <Modal
        opened={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        title="Update Status"
        centered
      >
        <Stack gap="md">
          <Select
            label="New Status"
            data={[
              { value: 'DRAFT', label: 'Draft' },
              { value: 'SUBMITTED', label: 'Submitted' },
              { value: 'UNDER_REVIEW', label: 'Under Review' },
              { value: 'APPROVED', label: 'Approved' },
              { value: 'REJECTED', label: 'Rejected' },
              { value: 'AWARDED', label: 'Awarded' },
            ]}
            value={form.status}
            onChange={(value) => value && handleStatusUpdate(value)}
          />
          <Group justify="flex-end">
            <Button variant="light" onClick={() => setEditModalOpen(false)}>
              Cancel
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Meeting Assignment Modal */}
      <Modal
        opened={meetingModalOpen}
        onClose={() => setMeetingModalOpen(false)}
        title="Assign to Bid Committee Meeting"
        centered
      >
        <Stack gap="md">
          <Select
            label="Select Meeting"
            placeholder="Choose a meeting"
            data={meetings?.map(meeting => ({
              value: meeting.id,
              label: `${meeting.title} - ${formatDate(meeting.plannedStart)}`,
            })) || []}
            value={selectedMeetingId}
            onChange={(value) => setSelectedMeetingId(value || '')}
          />
          <Group justify="flex-end">
            <Button variant="light" onClick={() => setMeetingModalOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleAssignToMeeting}
              disabled={!selectedMeetingId}
              loading={updateForm.isPending}
            >
              Assign
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        opened={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        title="Delete Single Source Form"
        centered
      >
        <Stack gap="md">
          <Text>
            Are you sure you want to delete the single source form{' '}
            <Text component="span" fw={500}>
              {form.formNumber}
            </Text>
            ? This action cannot be undone.
          </Text>
          <Group justify="flex-end">
            <Button variant="light" onClick={() => setDeleteModalOpen(false)}>
              Cancel
            </Button>
            <Button
              color="red"
              loading={deleteForm.isPending}
              onClick={handleDelete}
            >
              Delete
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Approve Modal */}
      <Modal
        opened={approveModalOpen}
        onClose={() => {
          setApproveModalOpen(false);
          setApprovalComments('');
        }}
        title="Approve Single Source Form"
        centered
      >
        <Stack gap="md">
          <Text>
            Are you sure you want to approve this single source form? This will mark it as approved.
          </Text>
          <Textarea
            label="Comments (Optional)"
            placeholder="Add any comments about your approval..."
            value={approvalComments}
            onChange={(e) => setApprovalComments(e.currentTarget.value)}
            minRows={3}
          />
          <Group justify="flex-end">
            <Button
              variant="light"
              onClick={() => {
                setApproveModalOpen(false);
                setApprovalComments('');
              }}
            >
              Cancel
            </Button>
            <Button
              color="green"
              loading={approveForm.isPending}
              onClick={handleApprove}
            >
              Approve
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Reject Modal */}
      <Modal
        opened={rejectModalOpen}
        onClose={() => {
          setRejectModalOpen(false);
          setRejectionComments('');
        }}
        title="Reject Single Source Form"
        centered
      >
        <Stack gap="md">
          <Text>
            Are you sure you want to reject this single source form? Please provide a reason for rejection.
          </Text>
          <Textarea
            label="Rejection Comments"
            placeholder="Please provide a reason for rejection..."
            value={rejectionComments}
            onChange={(e) => setRejectionComments(e.currentTarget.value)}
            minRows={3}
            required
            error={!rejectionComments.trim() ? 'Rejection comments are required' : null}
          />
          <Group justify="flex-end">
            <Button
              variant="light"
              onClick={() => {
                setRejectModalOpen(false);
                setRejectionComments('');
              }}
            >
              Cancel
            </Button>
            <Button
              color="red"
              loading={rejectForm.isPending}
              onClick={handleReject}
              disabled={!rejectionComments.trim()}
            >
              Reject
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Container>
  );
}
